import {
  Avatar,
  Badge,
  Button,
  Col,
  DatePicker,
  Descriptions,
  Input,
  message,
  Row,
  Space,
  Spin,
  Table,
} from "antd";
import React, { useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import {
  IconBlockLight,
  IconCopyDomain,
  IconLeft,
  IconRight,
  IconSearch,
  IconUnblockLight,
} from "../../../../assets/icons";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../../../api/api";
import moment from "moment";
import { getFormattedNumber } from "@utils";
import { ORDER_STATUS } from "../../../../constants";
import ConfirmModal from "../ConfirmModal/ConfirmModal";
import _ from "lodash";
import { UserOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

const RootContainer = styled.div``;

const Topbar = styled.div`
  padding-top: 8px;
  padding-bottom: 24px;
  display: flex;
  gap: 18px;
  align-items: center;
  justify-content: center;

  .ant-btn-default {
    height: 48px;
    padding: 0 12px !important;
    border: none !important;
    background: ${(props) => props.theme.backgroundColor} !important;
    color: ${(props) => props.theme.primaryColor} !important;
  }

  .ant-btn-primary {
    width: 172px;
    height: 48px;
  }
`;

const TitleWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 400;
  color: #1d1014;
  line-height: 1;
`;

const GrayTitle = styled(Title)`
  color: #868686;
`;

const Card = styled.div`
  padding: 16px;
  border-radius: 8px;
  background: ${(props) => props.theme.backgroundColor};
  height: 100%;
  .ant-avatar {
    width: 40%;
    height: auto;
    aspect-ratio: 1;
  }

  .ant-descriptions {
    width: 100%;

    .ant-descriptions-item-label,
    .ant-descriptions-item-content {
      width: 50%;
      font-size: 16px;
      font-weight: 400;
      color: #011333;
    }

    svg {
      cursor: pointer;
    }
  }

  .ant-row {
    .ant-col:first-child {
      text-align: center;
    }
    .ant-col:last-child {
      text-align: right;
    }
  }

  .ant-btn {
    width: 200px;
    height: 48px;
    color: #fff !important;
    font-size: 16px !important;
    font-weight: 600 !important;
  }

  .ant-btn-primary {
    background: #f00 !important;
  }

  .ant-btn-default {
    background: #009800 !important;
  }
`;

const ActionsWrapper = styled(Space)`
  .ant-btn-default {
    width: 172px;
    height: 32px;
    background: ${(props) => props.theme.backgroundColor};
    color: ${(props) => props.theme.primaryColor} !important;
    border: none;
    font-size: 14px;
    font-weight: 600;

    :hover {
      background: ${(props) => props.theme.backgroundColor} !important;
    }
  }

  .ant-btn-dangerous {
    background: #ffe6e8 !important;
    color: #f00 !important;

    :hover {
      background: #ffe6e8 !important;
    }
  }

  svg {
    cursor: pointer;
  }
`;

const Container = styled.div`
  .ant-picker {
    height: 48px;
  }
`;

const SpinWrapper = styled.div`
  text-align: center;
`;

const Text = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: #1d1014;
  // white-space: nowrap;
`;

const BoldText = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #1d1014;
`;

const Details = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [orderLoading, setOrderLoading] = useState(true);
  const [data, setData] = useState({});
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const [dates, setDates] = useState([]);
  const [keyword, setKeyword] = useState("");

  const {t} = useTranslation();

  useEffect(() => {
    getUserDetails();
    getUserOrders(keyword, dates);

    // eslint-disable-next-line
  }, [keyword, dates]);

  const getUserDetails = () => {
    api
      .getUserDetails(id)
      .then(({ data }) => {
        setLoading(false);
        setData(data);
      })
      .catch((error) => {
        message.error(error);
      });
  };

  const debouncedResults = useMemo(() => {
    return _.debounce((e) => {
      setKeyword(e.target.value.trim());
    }, 1000);
  }, []);

  const getUserOrders = (keyword, dates) => {
    let dateParams = {};
    if (Array.isArray(dates) && dates.length >= 2) {
      // Check if dates is an array with at least 2 elements
      const [startDate, endDate] = dates;
      if (startDate) {
        dateParams.date_from = moment(startDate.toISOString()).format("YYYY-MM-DD");
      }
      if (endDate) {
        dateParams.date_to = moment(endDate.toISOString()).format("YYYY-MM-DD")
      }
    }
    api
      .getUserOrders({
        id,
        keyword,
        ...dateParams,
      })
      .then(({ data }) => {
        setOrderLoading(false);
        setOrders(data);
      })
      .catch((error) => {
        setOrderLoading(false);
        message.error(error);
      });
  };

  const successCallback = () => {
    setData((prev) => {
      return {
        ...prev,
        user: {
          ...prev.user,
          status: !prev.user.status,
        },
      };
    });
  };

  const handleDateChange = (dates) => {
    setDates(dates ? dates : []);
  };

  const goBack = () => {
    navigate(-1);
  };

  const Loader = () => (
    <SpinWrapper>
      <Spin />
    </SpinWrapper>
  );

  const getOrderStatus = (status) => {
    switch (status) {
      case ORDER_STATUS.PENDING:
        return <Badge color={"orange"} text={t('pending')} />;
      case ORDER_STATUS.UNPAID:
        return <Badge color={"purple"} text={t('unpaid')} />;
      case ORDER_STATUS.CANCELLED:
        return <Badge color={"red"} text={t('cancelled')} />;
      case ORDER_STATUS.CONFIRMED:
        return <Badge color={"green"} text={t('confirmed')} />;
      case ORDER_STATUS.COMPLETED:
        return <Badge color={"blue"} text={t('pickup')} />;
      default:
        return "";
    }
  };

  const goToDetails = (id) => {
      navigate(`/home/order-mgnt/orders/PENDING/order-details/${id}`);
      // setSavedOrderPage(sortPage);
  };

  const columns = [
    {
      key: "created_at",
      dataIndex: "created_at",
      title: t('date_time'),
      render: (_, record) =>
        moment(record.created_at).format("DD MMM YYYY, HH:mm"),
    },
    {
      key: "order_id",
      dataIndex: "order_id",
      title: t('order_id'),
    },
    {
      key: "total_price",
      dataIndex: "total_price",
      title: t('products_amount'),
      render: (_, record) =>
        `${getFormattedNumber(record.total_quantity)} / ${getFormattedNumber(
          record.total_price
        )}`,
    },
    {
      key: "status",
      dataIndex: "status",
      title: t('status'),
      render: (_, record) => getOrderStatus(record.status),
    },
    {
          key: "actions",
          title: t('actions'),
          render: (_, record) => (
            <ActionsWrapper>
              {/* <Actions item={record} /> */}
              <Button
                style={{ backgroundColor: "#F0F6FE", color: "#005DFF" }}
                onClick={() => goToDetails(record.id)}
                type="text"
              >
                {t('view_details')}
              </Button>
              <IconRight onClick={() => goToDetails(record.id)} />
            </ActionsWrapper>
          ),
    },
  ];


  return (
    <RootContainer>
      <Topbar>
        <Button type="default" icon={<IconLeft />} onClick={goBack}>
          {t('back')}
        </Button>
        <TitleWrapper>
          <GrayTitle>{t('customers')}</GrayTitle>
          <IconRight />
          <Title>{t('customer_details')}</Title>
        </TitleWrapper>
      </Topbar>
      {loading ? (
        <Loader />
      ) : (
        <Container>
          <Row gutter={[12, 12]}>
            <Col xs={24}>
              <Card>
                <Row gutter={[12, 12]}>
                  <Col xs={24} lg={4}>
                    <Avatar
                      style={{ width: "100px" }}
                      shape="square"
                      src={data.user?.social_profile || data.user?.profile_url}
                      icon={!data.user?.social_profile && !data.user?.profile_url ? <UserOutlined style={{fontSize:"36px"}} /> : undefined}
                    />
                  </Col>
                  <Col xs={24} lg={12}>
                    <Descriptions column={1}>
                      <Descriptions.Item label={t('name')}>
                        {data.user?.custom_name}
                      </Descriptions.Item>
                      <Descriptions.Item label={t('customer_id')}>
                        <Space>
                          {data.user?.username}
                          <IconCopyDomain
                            onClick={() => {
                              navigator.clipboard
                                .writeText(data.user?.username)
                                .then(() => message.success("Copied!"));
                            }}
                          />
                        </Space>
                      </Descriptions.Item>
                      <Descriptions.Item label={t('email_phone')}>
                        {data.user?.email || data?.user?.phone ||  "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label={t('login_with')}>
                        {data.user?.login_type}
                      </Descriptions.Item>
                      <Descriptions.Item label={t('joined_date')}>
                        {moment(data.user?.created_at).format(
                          "DD MMM YYYY, HH:mm"
                        )}
                      </Descriptions.Item>
                    </Descriptions>
                  </Col>
                  <Col xs={24} lg={8}>
                    {data?.user?.status ? (
                      <Button
                        type="primary"
                        icon={<IconBlockLight />}
                        onClick={() => setOpen(true)}
                      >
                        {t('block')}
                      </Button>
                    ) : (
                      <Button
                        type="default"
                        icon={<IconUnblockLight />}
                        onClick={() => setOpen(true)}
                      >
                        {t('unblock')}
                      </Button>
                    )}
                  </Col>
                </Row>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Card>
                <Space direction="vertical">
                  <Text>{t('total_orders')}</Text>
                  <BoldText>{getFormattedNumber(data.totalOrders)}</BoldText>
                </Space>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Card>
                <Space direction="vertical">
                  <Text>{t('total_confirmed_order')}</Text>
                  <BoldText>{getFormattedNumber(data.confirmOrders)}</BoldText>
                </Space>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Card>
                <Space direction="vertical">
                  <Text>{t('total_pickup_order')}</Text>
                  <BoldText>{getFormattedNumber(data.pickedupOrders)}</BoldText>
                </Space>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Card>
                <Space direction="vertical">
                  <Text>{t('total_cancelled_orders')}</Text>
                  <BoldText>
                    {getFormattedNumber(data.cancelledOrders)}
                  </BoldText>
                </Space>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Card>
                <Space direction="vertical">
                  <Text>{t('total_pending_order')}</Text>
                  <BoldText>{getFormattedNumber(data.pendingOrders)}</BoldText>
                </Space>
              </Card>
            </Col>
             <Col xs={24} sm={12} md={8} lg={4}>
              <Card>
                <Space direction="vertical">
                  <Text>{t('total_unpaid_order')}</Text>
                  <BoldText>{getFormattedNumber(data?.unpaidOrders) || 0}</BoldText>
                </Space>
              </Card>
            </Col>
            <Row gutter={[12, 12]} style={{ width: "100%" }}>
              <Col xs={24} md={8}>
                <Input
                  placeholder={t('search_by_order_id')}
                  prefix={<IconSearch />}
                  onChange={debouncedResults}
                  allowClear
                />
              </Col>
              <Col xs={0} md={8} />
              <Col xs={24} md={8} style={{ textAlign: "right" }}>
                <DatePicker.RangePicker
                  style={{ width: "100%" }}
                  placeholder={[t('start_date'), t('end_date')]}
                  format={"DD MMM YYYY"}
                  onChange={handleDateChange}
                />
              </Col>
            </Row>
            <Col xs={24}>
              <Table
                dataSource={orders}
                columns={columns}
                loading={orderLoading}
                pagination={false}
              />
            </Col>
          </Row>
        </Container>
      )}

      <ConfirmModal
        open={open}
        id={id}
        status={data.user?.status}
        handleClose={() => setOpen(false)}
        successCallback={successCallback}
      />
    </RootContainer>
  );
};

export default Details;
